<script setup>
import {
  computed,
  onBeforeMount,
  watch,
  ref,
  useHead,
  useRoute,
  useSanity,
  useSanityQuery,
  useScrollTopOnMounted,
  useDevice,
  useRouter,
  useEventTracking,
} from '#imports';
import { getCommonMetaTags } from '~/utils/get-common-meta-tags';
import NewsPageSlider from '~/components/news-page/news-page-slider/news-page-slider.vue';
import { getQuery as getHighlightNews } from '~/groq-queries/main-news-card';
import { getQuery as getNews } from '~/groq-queries/news';
import { getQuery as getNewsChronicles } from '~/groq-queries/news-chronicles';
import { useFetchMoreData } from '@/composables/use-fetch-more-data';
import NewsPageHeader from '~/components/news-page/news-page-header/news-page-header.vue';
import NewsList from '~/components/news-page/news-list/news-list.vue';
import { getQuery as getNewsByTagQuery } from '~/groq-queries/news-by-tag';
import LayoutNewsPage from '@/components/layout/news-page.vue';
import NewsMenuMobile from '~/components/news-page/news-menu-mobile/news-menu-mobile.vue';
import NewsAside from '~/components/news-page/news-aside/news-aside.vue';
import LoaderIndicator from '~/components/loader-indicator/loader-indicator.vue';
import { tags } from '~/constants/tags';
import { getQuery as getSearch } from '~/groq-queries/search-news';

useScrollTopOnMounted();
const route = useRoute();

const title = 'Все новости - DOXA';
const description = 'Здесь можно посмотреть все новости DOXA';
const NEWS_URL = 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app/news';

useHead({
  title,
  meta: getCommonMetaTags({
    path: route.fullPath,
    ogType: 'website',
    title,
    description,
  }),
  script: [
    {
      key: 'ld-json-news',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url: NEWS_URL,
        mainEntity: {
          '@type': 'ItemList',
          name: 'Новости DOXA',
          description: 'Все новости и сводки от медиа DOXA',
          url: NEWS_URL,
        },
        isPartOf: {
          '@type': 'WebSite',
          name: 'DOXA',
          url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': NEWS_URL,
        },
      }),
    },
  ],
});

const router = useRouter();
const sanity = useSanity();
const { isMobile } = useDevice();
const newsChunkSize = 12;
const offset = ref(0);
const visibleItemsCount = ref(newsChunkSize);
const tag = ref(null);
const isAllNews = ref(true);
const searchQuery = ref(null);
const searchResults = ref([]);
const searchPerformed = ref(false);
const isShowMoreButton = ref(true);
const isLoading = ref(false);
const newsByTag = ref(null);
const errorMessage = ref(null);
const newsQuery = getNews({
  limit: visibleItemsCount.value,
  offset: offset.value,
});

const newsChroniclesQuery = getNewsChronicles({
  limit: 30,
  offset: 0,
});
const routeTag = computed(() => router.currentRoute.value.query.tag);
const highlightNewsQuery = getHighlightNews();
const fetchQuery = (params) => sanity.fetch(getNews(params));
const fetchTagNews = (params) => sanity.fetch(getNewsByTagQuery(params));
const isEmptyResults = computed(() => searchResults.value.length === 0);

const isMoreNewsButton = computed(
  () => searchResults.value.length > visibleItemsCount.value
);

const searchResultsReduced = computed(() =>
  searchResults.value.slice(0, visibleItemsCount.value)
);

const showMore = () => {
  visibleItemsCount.value += newsChunkSize;
};

const [
  { data: highlightNewsData },
  { data: newsData },
  { data: newsChroniclesData },
] = await Promise.all([
  useSanityQuery(highlightNewsQuery),
  useSanityQuery(newsQuery),
  useSanityQuery(newsChroniclesQuery),
]);
const { fetchMoreData: fetchMoreDataByTag, loading: isTagsLoading } =
  useFetchMoreData(newsByTag, newsChunkSize, fetchTagNews, tag);

const { fetchMoreData, loading: isMoreNewsLoading } = useFetchMoreData(
  newsData,
  newsChunkSize,
  fetchQuery
);

const headerText = computed(() =>
  tag.value === 'Эксклюзив DOXA'
    ? `Эксклюзивы`
    : `Новости по тегу «${tag.value}»`
);

const handleError = (error_) => {
  errorMessage.value = error_.message;
};

const performSearch = async (newSearchValue) => {
  useEventTracking('Search', `User used search on news page`);

  searchQuery.value = newSearchValue;
  searchPerformed.value = true;
  isLoading.value = true;

  const { query } = getSearch({ searchQuery: newSearchValue.trim() });

  try {
    const { data: result } = await useSanityQuery(query);
    searchResults.value = result.value;
  } catch (error) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

const updateTagNews = async (newTag) => {
  isLoading.value = true;
  router.push({ query: { tag: newTag } });
  const query = getNewsByTagQuery({
    tag: newTag,
    offset: 0,
    limit: visibleItemsCount.value,
  });
  isAllNews.value = newTag === null;
  searchQuery.value = null;
  searchPerformed.value = false;

  try {
    const { data: updatedData } = await useSanityQuery(query);
    newsByTag.value = updatedData.value;
    offset.value = 0;
    if (newsByTag.value.length > newsChunkSize) {
      newsByTag.value.pop();
      isShowMoreButton.value = true;
    } else {
      isShowMoreButton.value = false;
    }
  } catch (error) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

const handleEnd = () => {
  fetchMoreData();
};

const handleFetchTag = (tagData) => {
  tag.value = tagData;
};

const routeToAllNews = () => {
  router.push({ query: {} });
  tag.value = null;
  searchQuery.value = null;
  searchPerformed.value = false;
};

const handleBack = () => {
  routeToAllNews();
};

watch(
  () => tag.value,
  (newTag) => {
    if (newTag !== null) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      updateTagNews(newTag);
      isAllNews.value = false;
    } else {
      isAllNews.value = true;
    }
  }
);

watch(searchQuery, (newSearchQuery) => {
  if (newSearchQuery !== null) {
    searchQuery.value = newSearchQuery;
    performSearch(newSearchQuery);
    isAllNews.value = false;
  } else {
    isAllNews.value = true;
  }
});

onBeforeMount(() => {
  if (routeTag.value !== undefined) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    handleFetchTag(routeTag.value);
  } else {
    routeToAllNews();
  }
});

watch(
  () => routeTag,
  (newTag) => {
    if (newTag !== undefined) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      updateTagNews(newTag);
    } else {
      routeToAllNews();
    }
  }
);
</script>

<template>
  <LayoutNewsPage is-animated-header is-header-shown is-hide-donation-form>
    <template #page-header>
      <NewsPageHeader
        v-if="isAllNews && tag === null && !searchPerformed"
        show-svg-title
        :highlight-news-data="highlightNewsData"
      />

      <NewsPageHeader
        v-if="tag !== null && !searchPerformed"
        with-back-button
        :header-text="headerText"
        @back="handleBack"
      />

      <NewsPageHeader
        v-if="searchPerformed"
        with-back-button
        with-search
        :search-query="searchQuery"
        @back="handleBack"
        @search="performSearch"
      />
    </template>

    <template #page-content>
      <main v-if="isMobile">
        <div class="news news--mobile">
          <LoaderIndicator v-if="isLoading" />

          <NewsPageSlider
            v-if="!isLoading && isAllNews && tag === null && !searchPerformed"
            :news-data="newsData"
            @fetch="handleEnd"
          />

          <NewsList
            v-if="!isLoading && !isAllNews && tag !== null && !searchPerformed"
            :data="newsByTag"
            :is-loading="isTagsLoading"
            :is-show-more-button="isShowMoreButton"
            @fetch="fetchMoreDataByTag"
          />

          <NewsList
            v-if="!isLoading && searchResults"
            :data="searchResultsReduced"
            :highlighted="searchQuery"
            :is-show-more-button="isMoreNewsButton"
            :is-loading="isLoading"
            :is-empty-results="searchQuery && isEmptyResults"
            @fetch="showMore"
          />
        </div>
      </main>

      <div v-if="!isMobile" class="news news--desktop">
        <main class="news__container">
          <LoaderIndicator v-if="isLoading" />
          <NewsList
            v-if="!isLoading && !isAllNews && tag !== null && !searchPerformed"
            :data="newsByTag"
            :is-loading="isTagsLoading"
            :is-show-more-button="isShowMoreButton"
            @fetch="fetchMoreDataByTag"
          />

          <NewsList
            v-if="!isLoading && isAllNews && !searchPerformed"
            :data="newsData"
            :chronicles-data="newsChroniclesData"
            :is-loading="isMoreNewsLoading"
            :is-show-more-button="isShowMoreButton"
            @fetch="fetchMoreData"
          />

          <NewsList
            v-if="!isLoading && searchResults"
            :data="searchResultsReduced"
            :highlighted="searchQuery"
            :is-empty-results="isEmptyResults"
            :is-show-more-button="isMoreNewsButton"
            :is-loading="isLoading"
            @fetch="showMore"
          />
        </main>
        <NewsAside
          v-if="!searchPerformed"
          :tags="tags"
          @fetch-tag="handleFetchTag"
          @search="performSearch"
          @reset-tag="routeToAllNews"
        />
      </div>

      <NewsMenuMobile
        v-if="isMobile"
        :tags="tags"
        @fetch-tag="handleFetchTag"
        @search="performSearch"
      />
    </template>
  </LayoutNewsPage>
</template>

<style scoped lang="postcss">
main {
  min-height: 500px;
}

.news {
  margin: 16px 0;

  .news__container {
    min-height: 500px;
    max-width: 650px;
    width: 100%;
    margin-top: 20px;

    @media (max-width: 768px) {
      max-width: 420px;
    }
  }

  &__loader {
    min-height: 500px;
    width: 100%;
  }

  &--desktop {
    display: flex;
    flex-direction: row;
    gap: 24px;

    @media (min-width: 1024px) {
      margin: 16px 0 100px 0;
    }
  }
}
</style>
